import { Container, Graphics, Sprite, Stage } from '@pixi/react'
import '@pixi/events'
import React, {
    createContext,
    createRef,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react'
import { useGesture } from '@use-gesture/react'
import { w3cwebsocket as W3CWebSocket } from 'websocket'
import { calculateSizeOfPart, generateUUID } from './utils'
import { useMousePosition } from '../hooks/MouseHooks'
import { ADD, ART_ADJUST, SELECT } from './elements/Modes'
import Part from './parts/Part'
import ComplexPart from './parts/ComplexPart'
import Panel from './parts/Panel'
import Grid from './Grid'
import { editLockComponentAtom } from '../atoms/ViewOptions.atom'
import { useAtom } from 'jotai'
import { usePartTable } from '../hooks/PartTableHooks'

export const ButtonStatusContext = createContext()
export const StageReadyContext = createContext(false)

export const useButtonStatus = (part) => {
    const buttonsPressed = useContext(ButtonStatusContext)

    if (part.type !== 'button') {
        return false
    }

    return buttonsPressed?.find(({ id }) => id === part.id)
}

const timeThreshold = 100

const LayoutDisplay = ({
    layout,
    currentScale,
    screenWidth,
    screenHeight,
    selected,
    hovered,
    mode,
    workspaceDimensions,
    workspacePosition,
    workspaceRef,
    placingPartId,
    placingPartType,
    preview,
    artDataUri,
    artMasked,
    onSelectPart,
    onHoverPart,
    onSecondarySelectPart,
    onLayoutChange,
}) => {
    const { partTable } = usePartTable()
    const componentRef = createRef()
    const stageRef = useRef()
    const [stageReady, setStageReady] = useState(false)

    const websocket = useRef()
    const controllerId = useRef()

    const [editLock] = useAtom(editLockComponentAtom)

    const [selectedIndex, setSelectedIndex] = useState(-1)

    const [mouseX, mouseY] = useMousePosition(workspaceRef)
    const [isDragging, setIsDragging] = useState(false)
    const [dragStartPartPosition, setDragStartPartPosition] = useState(null)
    const [dragStartPartId, setDragStartPartId] = useState(null)
    const [artworkDragStart, setArtworkDragStart] = useState(null)
    const [artImageSize, setArtImageSize] = useState(null)
    const cornerDragRef = useRef(null)
    const artStateRef = useRef(null)
    const [artMaskGraphics, setArtMaskGraphics] = useState(null)

    const bind = useGesture(
        {
            onDrag: ({
                xy,
                movement: [dx, dy],
                offset: [artworkOffsetX, artworkOffsetY],
                dragging,
                first,
                touches,
                buttons,
                shiftKey,
                elapsedTime,
            }) => {
                setIsDragging(dragging)

                if (editLock || (mode !== SELECT && mode !== ART_ADJUST)) {
                    return
                }

                if (mode === ART_ADJUST) {
                    if (cornerDragRef.current) {
                        return
                    }
                    if (first) {
                        setArtworkDragStart(layout.artworkOffset || [0, 0])
                        return
                    }
                    if (
                        dragging &&
                        elapsedTime > timeThreshold &&
                        !shiftKey &&
                        (touches === 1 || buttons === 1)
                    ) {
                        const start = artworkDragStart ||
                            layout.artworkOffset || [0, 0]
                        onLayoutChange({
                            ...layout,
                            artworkOffset: [
                                start[0] + dx / currentScale,
                                start[1] + dy / currentScale,
                            ],
                        })
                    }
                    return
                }

                if (
                    elapsedTime > timeThreshold &&
                    !shiftKey &&
                    dragging &&
                    (touches === 1 || buttons === 1) &&
                    selectedIndex >= 0 &&
                    selectedIndex < layout.parts.length
                ) {
                    // If dragStartPartPosition is null, initialize it now
                    let currentDragStartPosition = dragStartPartPosition
                    if (!currentDragStartPosition) {
                        const selectedPart = layout.parts[selectedIndex]
                        currentDragStartPosition = [...selectedPart.position]
                        setDragStartPartPosition(currentDragStartPosition)
                        setDragStartPartId(selectedPart.id)
                    }

                    let updatedParts = [...layout.parts]
                    let found = { ...layout.parts[selectedIndex] }

                    if (found && !found?.relativeTo) {
                        // Use movement deltas instead of absolute coordinates
                        const scaledDx = dx / currentScale
                        const scaledDy = dy / currentScale

                        found = {
                            ...found,
                            position: [
                                Math.trunc(
                                    currentDragStartPosition[0] + scaledDx
                                ),
                                Math.trunc(
                                    currentDragStartPosition[1] + scaledDy
                                ),
                            ],
                            // Preserve existing origin and anchor
                            origin: found.origin || [0, 0],
                            anchor: found.anchor || [0.5, 0.5],
                        }

                        updatedParts[selectedIndex] = found
                        onLayoutChange({ ...layout, parts: updatedParts })
                    }
                }
            },
            onPinch: ({ offset: [d], memo }) => {
                if (editLock || mode !== ART_ADJUST) {
                    return
                }

                if (!memo) {
                    memo = layout.artworkZoom
                }

                onLayoutChange({
                    ...layout,
                    artworkZoom: Math.max(0.1, memo * d),
                })
                return memo
            },
        },
        {
            drag: {
                pointer: {
                    touch: true,
                    mouse: true,
                },
            },
            pinch: {
                pointer: {
                    touch: true,
                    mouse: true,
                },
            },
        }
    )
    const [buttonsPressed, setButtonsPressed] = useState([])

    const [websocketIp, setWebsocketIp] = useState('localhost:3001')
    const [connected, setConnected] = useState(false)

    const isMoving = useRef(false)

    const connect = () => {
        const ws = new W3CWebSocket(`ws://${websocketIp}`)
        controllerId.current = generateUUID()

        //Register player
        ws.onopen = () => {
            websocket.current = ws
            setConnected(true)
        }

        ws.onmessage = (message) => {}

        ws.onclose = (e) => {}

        ws.onerror = (err) => {}
    }

    const updateRemoteButtons = (buttons) => {
        if (!websocket.current) {
            return
        }

        const message = {
            controllerId: controllerId.current,
            buttons: buttons.map(({ id, mapping }) => ({ id, mapping })),
        }
        websocket.current.send(JSON.stringify(message))
    }

    const addPart = useCallback(
        (evt) => {
            if (mode !== ADD) {
                return
            }

            const name = partTable?.[placingPartType]?.[placingPartId].name
            const defaultAnchor = [0.5, 0.5]

            // Get the part template for size calculation
            let partTemplate = {
                type: placingPartType,
                partId: placingPartId,
                anchor: defaultAnchor,
            }

            // If it's a user part (complex part), include the model tree for proper size calculation
            if (placingPartType === 'user') {
                partTemplate = {
                    ...partTable['user'][placingPartId],
                    ...partTemplate,
                }
            }

            // Calculate part size for anchor positioning
            const partSize = calculateSizeOfPart(partTemplate, partTable)

            // Only apply anchor adjustments for custom and user parts
            // Basic parts should be positioned directly at their center
            let anchorAdjustmentX = 0
            let anchorAdjustmentY = 0
            if (placingPartType === 'custom' || placingPartType === 'user') {
                anchorAdjustmentX = defaultAnchor[0] * partSize[0]
                anchorAdjustmentY = defaultAnchor[1] * partSize[1]
            }

            // Position the part so that after anchor adjustment, it appears at click location
            const clickWorldX =
                (evt.offsetX - workspacePosition[0]) / currentScale
            const clickWorldY =
                (evt.offsetY - workspacePosition[1]) / currentScale

            const partsCopy = [...layout.parts]
            let newPart = {
                id: generateUUID(),
                name,
                type: placingPartType,
                partId: placingPartId,
                position: [
                    Math.trunc(clickWorldX + anchorAdjustmentX),
                    Math.trunc(clickWorldY + anchorAdjustmentY),
                ],
                origin: [0, 0],
                anchor: defaultAnchor, // Default anchor to center
            }

            partsCopy.push(newPart)
            const updatedLayout = { ...layout, parts: partsCopy }
            onLayoutChange(updatedLayout)
        },
        [
            layout,
            onLayoutChange,
            mode,
            currentScale,
            placingPartId,
            placingPartType,
            workspacePosition,
            partTable,
        ]
    )

    const handlePointerDown = useCallback((event) => {
        isMoving.current = false
    }, [])

    const handlePointerMove = useCallback((event) => {
        isMoving.current = true
    }, [])

    const handlePointerUp = useCallback(
        (event) => {
            if (!isMoving.current || event.pointerType === 'mouse') {
                addPart(event)
            }
        },
        [addPart]
    )

    useEffect(() => {
        const ele = workspaceRef.current

        ele.addEventListener('pointerdown', handlePointerDown)
        ele.addEventListener('pointermove', handlePointerMove)
        ele.addEventListener('pointerup', handlePointerUp)
        return () => {
            ele.removeEventListener('pointerdown', handlePointerDown)
            ele.removeEventListener('pointermove', handlePointerMove)
            ele.removeEventListener('pointerup', handlePointerUp)
        }
    }, [
        addPart,
        handlePointerDown,
        handlePointerMove,
        handlePointerUp,
        workspaceRef,
    ])

    useEffect(() => {
        const index = layout.parts?.findIndex(({ id }) => id === selected)
        setSelectedIndex(index)

        // Reset drag state if we're switching to a different part
        if (selected !== dragStartPartId) {
            setDragStartPartPosition(null)
            setDragStartPartId(null)
        }
    }, [selected, layout.parts, setSelectedIndex, dragStartPartId])

    useEffect(() => {
        if (!isDragging) {
            setButtonsPressed([])
        }
    }, [isDragging])

    useEffect(() => {
        if (!artDataUri) {
            setArtImageSize(null)
            return
        }
        const img = new window.Image()
        img.onload = () => {
            setArtImageSize([img.naturalWidth, img.naturalHeight])
            const { layout: l, onLayoutChange: onChange } = artStateRef.current
            const imgW = img.naturalWidth
            const imgH = img.naturalHeight
            const panelW = l.panelDimensions?.[0]
            const panelH = l.panelDimensions?.[1]
            if (panelW && panelH) {
                const artworkZoom = Math.max(panelW / imgW, panelH / imgH)
                const offsetX = (panelW - imgW * artworkZoom) / 2
                const offsetY = (panelH - imgH * artworkZoom) / 2
                onChange({
                    ...l,
                    artworkZoom,
                    artworkOffset: [offsetX, offsetY],
                })
            }
        }
        img.src = artDataUri
    }, [artDataUri])

    useEffect(() => {
        const onMove = (e) => {
            if (!cornerDragRef.current) return
            const { corner, imgW, imgH, fixedX, fixedY, startOffset } =
                cornerDragRef.current
            const {
                workspacePosition: wp,
                currentScale: cs,
                layout: l,
                onLayoutChange: onChange,
            } = artStateRef.current

            const mx = e.clientX - wp[0]

            let newZoom =
                corner === 'br' || corner === 'tr'
                    ? (mx - fixedX) / (imgW * cs)
                    : (fixedX - mx) / (imgW * cs)
            newZoom = Math.max(0.01, newZoom)

            let [newOffsetX, newOffsetY] = startOffset
            if (corner === 'tl' || corner === 'bl') {
                newOffsetX = fixedX / cs - imgW * newZoom
            }
            if (corner === 'tl' || corner === 'tr') {
                newOffsetY = fixedY / cs - imgH * newZoom
            }

            onChange({
                ...l,
                artworkZoom: newZoom,
                artworkOffset: [newOffsetX, newOffsetY],
            })
        }
        const onUp = () => {
            cornerDragRef.current = null
        }
        window.addEventListener('pointermove', onMove)
        window.addEventListener('pointerup', onUp)
        return () => {
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('pointerup', onUp)
        }
    }, [])

    // Update ref each render so event handlers always see latest values
    artStateRef.current = {
        workspacePosition,
        currentScale,
        layout,
        onLayoutChange,
    }

    let component
    switch (mode) {
        case ADD:
            // For preview positioning, we need to account for how Part component
            // will adjust the position based on anchor
            const defaultAnchor = [0.5, 0.5]

            // Get the part template for size calculation
            let previewPartTemplate = {
                type: placingPartType,
                partId: placingPartId,
                anchor: defaultAnchor,
            }

            // If it's a user part (complex part), include the model tree for proper size calculation
            if (placingPartType === 'user') {
                previewPartTemplate = {
                    ...partTable['user'][placingPartId],
                    ...previewPartTemplate,
                }
            }

            // Position the part so that its center appears at cursor
            const mouseWorldX = (mouseX - workspacePosition[0]) / currentScale
            const mouseWorldY = (mouseY - workspacePosition[1]) / currentScale

            const partSize = calculateSizeOfPart(previewPartTemplate, partTable)

            // Only apply anchor adjustments for custom and user parts
            // Basic parts should be positioned directly at their center
            let anchorAdjustmentX = 0
            let anchorAdjustmentY = 0
            if (placingPartType === 'custom' || placingPartType === 'user') {
                anchorAdjustmentX = defaultAnchor[0] * partSize[0]
                anchorAdjustmentY = defaultAnchor[1] * partSize[1]
            }

            // All parts now use the same positioning logic since basic parts ignore anchors
            const position = [
                mouseWorldX + anchorAdjustmentX,
                mouseWorldY + anchorAdjustmentY,
            ]

            const previewPart = {
                partId: placingPartId,
                type: placingPartType,
                position,
                origin: [0, 0],
                anchor: defaultAnchor,
                ...(placingPartType === 'user' && {
                    modelTree: partTable['user'][placingPartId].modelTree,
                }),
            }

            // Use the correct component type for preview
            const PartComponent =
                placingPartType === 'user' ? ComplexPart : Part
            component = (
                <PartComponent
                    scale={currentScale}
                    part={previewPart}
                    parent={{
                        parts: layout.parts,
                        panelDimensions: workspaceDimensions,
                    }}
                    onClick={() => {}}
                />
            )
            break
        default:
            break
    }

    return (
        <>
            {preview ? (
                <div className="absolute right-0 top-0">
                    <input
                        className="h-8 disabled:bg-gray-600"
                        value={websocketIp}
                        disabled={connected}
                        onChange={({ target: { value } }) =>
                            setWebsocketIp(value)
                        }
                    />
                    <button
                        className={`h-8 min-w-20 border-2 border-solid border-black bg-white p-1 disabled:bg-gray-600`}
                        onClick={connect}
                        disabled={connected}
                    >
                        Connect
                    </button>
                </div>
            ) : null}
            <Stage
                ref={stageRef}
                width={screenWidth}
                height={screenHeight}
                renderOnComponentChange={true}
                options={{ background: 0x1099bb }}
                onMount={() => setStageReady(true)}
                {...bind()}
            >
                <StageReadyContext.Provider value={stageReady}>
                    <Container
                        ref={componentRef}
                        x={workspacePosition[0]}
                        y={workspacePosition[1]}
                        sortChildren={true}
                    >
                        <Grid
                            scale={currentScale}
                            screenWidth={screenWidth}
                            screenHeight={screenHeight}
                            workspacePosition={workspacePosition}
                        />
                        {artDataUri && (
                            <>
                                {artMasked && (
                                    <Graphics
                                        ref={setArtMaskGraphics}
                                        draw={(g) => {
                                            g.clear()
                                            g.beginFill(0xffffff)
                                            g.drawRoundedRect(
                                                0,
                                                0,
                                                layout?.panelDimensions?.[0] *
                                                    currentScale,
                                                layout?.panelDimensions?.[1] *
                                                    currentScale,
                                                layout?.cornerRadius *
                                                    currentScale || 0
                                            )
                                            g.endFill()
                                        }}
                                    />
                                )}
                                <Sprite
                                    image={artDataUri}
                                    x={
                                        layout.artworkOffset?.[0] *
                                            currentScale || 0
                                    }
                                    y={
                                        layout.artworkOffset?.[1] *
                                            currentScale || 0
                                    }
                                    anchor={0}
                                    scale={
                                        currentScale * (layout.artworkZoom || 1)
                                    }
                                    mask={artMasked ? artMaskGraphics : null}
                                />
                            </>
                        )}
                        {artDataUri &&
                            artImageSize &&
                            (() => {
                                const [imgW, imgH] = artImageSize
                                const artZoom = layout.artworkZoom || 1
                                const offset = layout.artworkOffset || [0, 0]
                                const cs = currentScale
                                const artLeft = offset[0] * cs
                                const artTop = offset[1] * cs
                                const artW = imgW * artZoom * cs
                                const artH = imgH * artZoom * cs
                                const artRight = artLeft + artW
                                const artBottom = artTop + artH
                                const r = 6
                                const corners = [
                                    {
                                        key: 'tl',
                                        x: artLeft,
                                        y: artTop,
                                        fx: artRight,
                                        fy: artBottom,
                                        cursor: 'nwse-resize',
                                    },
                                    {
                                        key: 'tr',
                                        x: artRight,
                                        y: artTop,
                                        fx: artLeft,
                                        fy: artBottom,
                                        cursor: 'nesw-resize',
                                    },
                                    {
                                        key: 'bl',
                                        x: artLeft,
                                        y: artBottom,
                                        fx: artRight,
                                        fy: artTop,
                                        cursor: 'nesw-resize',
                                    },
                                    {
                                        key: 'br',
                                        x: artRight,
                                        y: artBottom,
                                        fx: artLeft,
                                        fy: artTop,
                                        cursor: 'nwse-resize',
                                    },
                                ]
                                return (
                                    <>
                                        <Graphics
                                            draw={(g) => {
                                                g.clear()
                                                g.lineStyle(2, 0xffffff, 0.8)
                                                g.drawRect(
                                                    artLeft,
                                                    artTop,
                                                    artW,
                                                    artH
                                                )
                                            }}
                                        />
                                        {corners.map(
                                            ({ key, x, y, fx, fy, cursor }) => (
                                                <Graphics
                                                    key={key}
                                                    draw={(g) => {
                                                        g.clear()
                                                        g.beginFill(
                                                            0xffffff,
                                                            0.9
                                                        )
                                                        g.lineStyle(
                                                            1,
                                                            0x000000,
                                                            0.8
                                                        )
                                                        g.drawCircle(x, y, r)
                                                        g.endFill()
                                                    }}
                                                    eventMode="static"
                                                    cursor={cursor}
                                                    onpointerdown={() => {
                                                        cornerDragRef.current =
                                                            {
                                                                corner: key,
                                                                imgW,
                                                                imgH,
                                                                fixedX: fx,
                                                                fixedY: fy,
                                                                startOffset: [
                                                                    ...offset,
                                                                ],
                                                            }
                                                    }}
                                                />
                                            )
                                        )}
                                    </>
                                )
                            })()}
                        <Panel
                            scale={currentScale}
                            layout={layout}
                            fill="#000000"
                            onClick={() => {}}
                        />
                        {component}
                        <ButtonStatusContext.Provider value={buttonsPressed}>
                            {layout?.parts?.map((part, index) => (
                                <Part
                                    key={`part-${part.id || index}`}
                                    selectedPartId={selected}
                                    hoveredPartId={hovered}
                                    scale={currentScale}
                                    part={part}
                                    index={index}
                                    parent={layout}
                                    onHoverPart={onHoverPart}
                                    onClick={
                                        onSecondarySelectPart || onSelectPart
                                    }
                                    onClickPart={(part, action) => {
                                        if (
                                            part.type !== 'button' ||
                                            mode === ART_ADJUST
                                        ) {
                                            return
                                        }

                                        let old = [...buttonsPressed]
                                        if (action === 'DOWN') {
                                            if (
                                                !old.find(
                                                    ({ id }) => id === part.id
                                                )
                                            ) {
                                                old = [...buttonsPressed, part]
                                            }
                                        } else if (action === 'UP') {
                                            old = [...buttonsPressed].filter(
                                                ({ id }) => id !== part.id
                                            )
                                        }
                                        setButtonsPressed(old)
                                        updateRemoteButtons(old)
                                    }}
                                />
                            ))}
                        </ButtonStatusContext.Provider>
                    </Container>
                </StageReadyContext.Provider>
            </Stage>
        </>
    )
}

export default LayoutDisplay
